import { GraduationCap, Video, FileText, BookOpen, ExternalLink } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

interface CEUniversityProps {
    isOpen: boolean;
    onClose: () => void;
}

export function CEUniversity({ isOpen, onClose }: CEUniversityProps) {
    const caseStudies = [
        {
            id: 1,
            title: 'Case Study: Walmart Canada',
            description: 'Explore how Walmart Canada implemented circular economy practices with CHEP',
            category: 'Case Study'
        },
        {
            id: 2,
            title: 'Optimizing Supply Chain Efficiency: SpartanNash\'s Strategic Partnership with CHEP',
            description: 'Learn how SpartanNash optimized their supply chain through CHEP partnership',
            category: 'Case Study'
        }
    ];

    const circularEconomyVideos = [
        {
            id: 1,
            title: 'Introduction to Circular Economy',
            description: 'Learn the fundamentals of circular economy and its impact on sustainability',
            duration: '15 min',
            category: 'Circular Economy'
        }
        // More videos will be added here
    ];

    const webinars = [
        {
            id: 1,
            title: 'Extended Producer Responsibility US Part 1',
            description: 'Understanding EPR regulations and requirements in the United States',
            category: 'EPR Webinar'
        },
        {
            id: 2,
            title: 'Extended Producer Responsibility US Part 2',
            description: 'Advanced EPR implementation strategies for US businesses',
            category: 'EPR Webinar'
        },
        {
            id: 3,
            title: 'Extended Producer Responsibility CA Part 1',
            description: 'Overview of EPR regulations in Canada',
            category: 'EPR Webinar'
        },
        {
            id: 4,
            title: 'Extended Producer Responsibility CA Part 2',
            description: 'Implementing EPR compliance in Canadian operations',
            category: 'EPR Webinar'
        }
    ];

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <GraduationCap className="w-6 h-6 text-[#0072CE]" />
                        CE University - Circular Economy University
                    </DialogTitle>
                    <DialogDescription>
                        Access free educational resources on Circular Economy, EPR, and sustainable practices
                    </DialogDescription>
                </DialogHeader>

                <Tabs defaultValue="case-studies" className="mt-6">
                    <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="case-studies">Case Studies</TabsTrigger>
                        <TabsTrigger value="circular-economy">Circular Economy</TabsTrigger>
                        <TabsTrigger value="webinars">Webinars</TabsTrigger>
                    </TabsList>

                    {/* Case Studies Tab */}
                    <TabsContent value="case-studies" className="mt-6 space-y-4">
                        {caseStudies.map((caseStudy) => (
                            <div
                                key={caseStudy.id}
                                className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all duration-200 cursor-pointer group"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-[#0072CE] group-hover:bg-blue-100 transition-colors">
                                        <FileText className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-start justify-between gap-2">
                                            <h3 className="text-gray-900 group-hover:text-[#0072CE] transition-colors">
                                                {caseStudy.title}
                                            </h3>
                                            <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-[#0072CE] transition-colors flex-shrink-0" />
                                        </div>
                                        <p className="text-sm text-gray-600 mt-1">{caseStudy.description}</p>
                                        <div className="flex items-center gap-4 mt-2">
                                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                                {caseStudy.category}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </TabsContent>

                    {/* Circular Economy Tab */}
                    <TabsContent value="circular-economy" className="mt-6 space-y-4">
                        {circularEconomyVideos.map((video) => (
                            <div
                                key={video.id}
                                className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all duration-200 cursor-pointer group"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center text-green-600 group-hover:bg-green-100 transition-colors">
                                        <Video className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-start justify-between gap-2">
                                            <h3 className="text-gray-900 group-hover:text-[#0072CE] transition-colors">
                                                {video.title}
                                            </h3>
                                            <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-[#0072CE] transition-colors flex-shrink-0" />
                                        </div>
                                        <p className="text-sm text-gray-600 mt-1">{video.description}</p>
                                        <div className="flex items-center gap-4 mt-2">
                                            <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                                                {video.category}
                                            </span>
                                            {video.duration && (
                                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                                    <Video className="w-3 h-3" />
                                                    {video.duration}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* Note about more videos coming */}
                        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                            <p className="text-sm text-gray-600 text-center">
                                More videos coming soon...
                            </p>
                        </div>
                    </TabsContent>

                    {/* Webinars Tab */}
                    <TabsContent value="webinars" className="mt-6 space-y-4">
                        {webinars.map((webinar) => (
                            <div
                                key={webinar.id}
                                className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all duration-200 cursor-pointer group"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center text-purple-600 group-hover:bg-purple-100 transition-colors">
                                        <Video className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-start justify-between gap-2">
                                            <h3 className="text-gray-900 group-hover:text-[#0072CE] transition-colors">
                                                {webinar.title}
                                            </h3>
                                            <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-[#0072CE] transition-colors flex-shrink-0" />
                                        </div>
                                        <p className="text-sm text-gray-600 mt-1">{webinar.description}</p>
                                        <div className="flex items-center gap-4 mt-2">
                                            <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                                                {webinar.category}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </TabsContent>
                </Tabs>

                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                        <BookOpen className="w-5 h-5 text-[#0072CE] flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm text-gray-900">
                                <strong>New resources added monthly!</strong>
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                                Stay updated with the latest sustainability practices and circular economy insights.
                            </p>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
